import { api } from '@/Data/api';
import ss from '../Session';
import {
    LikeType,
    SocialResponse,
    CreateLikeParams,
    DeleteLikeParams
} from './types';

/**
 * Like a post
 * @param params CreateLikeParams object with postid
 * @returns Promise with like data or null if failed
 */
export const likePost = async (params: CreateLikeParams): Promise<LikeType | null> => {
    try {
        const res = await api.post<SocialResponse<LikeType>>('/social/like', params, {
            headers: {
                Authorization: `Bearer ${ss.getCurrentSession().jwt}`
            }
        });
        
        return res.data?.code === 200 ? (res.data.data ?? null) : null;
    } catch (e) {
        console.error('Failed to like post:', e);
        return null;
    }
};

/**
 * List likes for a post or by a user
 * @param postid Optional post ID to get likes for
 * @param userid Optional user ID to get likes by ('me' for current user)
 * @returns Promise with array of likes
 */
export const listLikes = async (postid?: string, userid?: string): Promise<LikeType[] | null> => {
    try {
        const params: any = {};
        if (postid) params.postid = postid;
        if (userid) params.userid = userid;
        
        const res = await api.get<SocialResponse<LikeType[]>>('/social/like', {
            params,
            headers: {
                Authorization: `Bearer ${ss.getCurrentSession().jwt}`
            }
        });
        
        return res.data?.code === 200 ? (res.data.data ?? null) : null;
    } catch (e) {
        console.error('Failed to list likes:', e);
        return null;
    }
};

/**
 * List likes for a specific post
 * @param postid Post ID to get likes for
 * @returns Promise with array of likes for the post
 */
export const listPostLikes = async (postid: string): Promise<LikeType[] | null> => {
    try {
        return await listLikes(postid);
    } catch (e) {
        console.error(`Failed to list likes for post ${postid}:`, e);
        return null;
    }
};

/**
 * List likes by a specific user
 * @param userid User ID to get likes by ('me' for current user)
 * @returns Promise with array of user's likes
 */
export const listUserLikes = async (userid: string = 'me'): Promise<LikeType[] | null> => {
    try {
        return await listLikes(undefined, userid);
    } catch (e) {
        console.error(`Failed to list likes for user ${userid}:`, e);
        return null;
    }
};

/**
 * List current user's likes
 * @returns Promise with array of current user's likes
 */
export const listMyLikes = async (): Promise<LikeType[] | null> => {
    try {
        return await listUserLikes('me');
    } catch (e) {
        console.error('Failed to list current user likes:', e);
        return null;
    }
};

/**
 * Unlike a post
 * @param params DeleteLikeParams object with postid
 * @returns Promise with boolean indicating success
 */
export const unlikePost = async (params: DeleteLikeParams): Promise<boolean> => {
    try {
        const res = await api.delete<SocialResponse<{message: string}>>('/social/like', {
            params,
            headers: {
                Authorization: `Bearer ${ss.getCurrentSession().jwt}`
            }
        });
        
        return res.data?.code === 200;
    } catch (e) {
        console.error('Failed to unlike post:', e);
        return false;
    }
};